// Lead Forms inline create + duplicate (Branch A — fetch-and-prefill). Pure
// helpers shared by the server (resourceService → Zernio) and the builder modal:
//
//   - toLeadFormDetail        : raw Zernio getLeadForm `form` → LeadFormDetail
//   - buildCreateLeadFormBody : CreateLeadFormInput → the Zernio createLeadForm body
//   - leadFormDetailToCreateInput : a fetched form → a prefilled duplicate draft
//   - validateCreateLeadFormInput : the business-rule guard (name / ≥1 question /
//                                   required https privacy URL)
//
// No React, no SDK, no Firestore — easy to unit-test against fixtures.

import type {
  CreateLeadFormInput,
  LeadFormDetail,
  LeadFormQuestion,
  LeadFormThankYou,
} from '../types/metaAdResources.js';

// The prefilled question types the builder's type dropdown offers. Meta auto-fills
// these from the user's profile, so they send TYPE ONLY (no key/label). Duplicate
// preserves whatever the source form had (incl. CUSTOM + any prefilled type Meta
// supports beyond this list) regardless of it.
export const PREFILLED_LEAD_FORM_QUESTION_TYPES = [
  'FULL_NAME',
  'FIRST_NAME',
  'LAST_NAME',
  'EMAIL',
  'PHONE',
] as const;
export type PrefilledLeadFormQuestionType = (typeof PREFILLED_LEAD_FORM_QUESTION_TYPES)[number];

export const PREFILLED_LEAD_FORM_QUESTION_LABELS: Record<string, string> = {
  FULL_NAME: 'Full name',
  FIRST_NAME: 'First name',
  LAST_NAME: 'Last name',
  EMAIL: 'Email',
  PHONE: 'Phone',
};

// A new create-from-scratch form starts with these (a sensible default; the AM
// adds/removes/reorders from here). Kept small so the default form isn't redundant
// (full name + first + last would be).
export const DEFAULT_LEAD_FORM_QUESTION_TYPES = ['FULL_NAME', 'EMAIL', 'PHONE'] as const;

/** A CUSTOM question (the only type that carries its own key/label/inline_context/options). */
export function isCustomQuestionType(type: string): boolean {
  return type.trim().toUpperCase() === 'CUSTOM';
}

// Human-readable label for a question type in the builder's type dropdown.
export function leadFormQuestionTypeLabel(type: string): string {
  const t = type.trim().toUpperCase();
  if (isCustomQuestionType(t)) return 'Custom question';
  return PREFILLED_LEAD_FORM_QUESTION_LABELS[t] ?? t;
}

// Default API name (field key) suggested from a CUSTOM question's label. It's an
// API name (flows into CRM/GHL), so it must be space-free — snake_case the label.
// The AM can override the suggestion.
export function slugifyLeadFormKey(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 64);
}

/** A blank CUSTOM question row (short-answer; options=null). */
export function emptyCustomQuestion(): LeadFormQuestion {
  return { type: 'CUSTOM', key: null, label: null, inlineContext: null, options: null };
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

// ---------- raw Zernio getLeadForm `form` → LeadFormDetail ----------
// getLeadForm returns the form untyped ({ [key]: unknown }) and Meta-native field
// names are snake_case, so read defensively and accept both snake/camel dialects.

function toQuestion(raw: unknown): LeadFormQuestion {
  const q = (raw ?? {}) as Record<string, unknown>;
  const type = (str(q.type) ?? '').toUpperCase();
  const optionsRaw = Array.isArray(q.options) ? q.options : null;
  const options = optionsRaw
    ? optionsRaw.map((o) => {
        const opt = (o ?? {}) as Record<string, unknown>;
        return { key: str(opt.key), value: str(opt.value) };
      })
    : null;
  return {
    type,
    key: str(q.key),
    label: str(q.label) ?? str(q.name), // Meta sometimes labels CUSTOM under `name`
    inlineContext: str(q.inline_context) ?? str(q.inlineContext),
    options: options && options.length > 0 ? options : null,
  };
}

function toThankYou(raw: unknown): LeadFormThankYou | null {
  const t = raw as Record<string, unknown> | null | undefined;
  if (!t || typeof t !== 'object') return null;
  const thankYou: LeadFormThankYou = {
    title: str(t.title),
    body: str(t.body),
    buttonText: str(t.button_text) ?? str(t.buttonText),
    buttonType: str(t.button_type) ?? str(t.buttonType),
    websiteUrl: str(t.website_url) ?? str(t.websiteUrl),
  };
  // All-null thank-you screen ≈ none — don't carry an empty object on duplicate.
  return Object.values(thankYou).some((v) => v !== null) ? thankYou : null;
}

export function toLeadFormDetail(formId: string, raw: unknown): LeadFormDetail {
  const f = (raw ?? {}) as Record<string, unknown>;
  const questionsRaw = Array.isArray(f.questions)
    ? f.questions
    : Array.isArray(f.fields)
      ? f.fields
      : [];
  // privacy_policy can be a flat `privacy_policy_url` or nested `privacy_policy.{url,link_text}`.
  const privacy = (f.privacy_policy ?? f.privacyPolicy) as Record<string, unknown> | undefined;
  return {
    id: str(f.id) ?? formId,
    name: str(f.name) ?? '',
    status: str(f.status),
    locale: str(f.locale),
    questions: questionsRaw.map(toQuestion).filter((q) => q.type !== ''),
    privacyPolicyUrl:
      str(f.privacy_policy_url) ?? str(f.privacyPolicyUrl) ?? (privacy ? str(privacy.url) : null),
    privacyPolicyLinkText:
      str(f.privacy_policy_link_text) ??
      str(f.privacyPolicyLinkText) ??
      (privacy ? str(privacy.link_text) ?? str(privacy.linkText) : null),
    followUpActionUrl: str(f.follow_up_action_url) ?? str(f.followUpActionUrl),
    thankYou: toThankYou(f.thank_you_page ?? f.thankYouPage ?? f.thankYou),
    isOptimizedForQuality: toIsOptimizedForQuality(f),
    // Raw passthrough — not authorable, carried so duplicate keeps the intro card.
    contextCard: f.context_card ?? f.contextCard ?? null,
  };
}

// Defensive parse of the form-type from a raw getLeadForm `form`. Meta has expressed
// this as a boolean (is_optimized_for_quality) and, on some shapes, a string
// form_type. Read both; HIGHER_INTENT / restricted → true, MORE_VOLUME / open →
// false, anything else unknown → null (treated as Open by the editor).
function toIsOptimizedForQuality(f: Record<string, unknown>): boolean | null {
  const bool = f.is_optimized_for_quality ?? f.isOptimizedForQuality;
  if (typeof bool === 'boolean') return bool;
  const formType = (str(f.form_type) ?? str(f.formType))?.toUpperCase();
  if (formType === 'HIGHER_INTENT') return true;
  if (formType === 'MORE_VOLUME') return false;
  return null;
}

// ---------- CreateLeadFormInput → Zernio createLeadForm body ----------
// The wire shape matches @zernio/node's CreateLeadFormData['body'] so the service
// passes it straight to ads.createLeadForm without a cast.

export type ZernioLeadFormQuestionBody = {
  type: string;
  key?: string;
  label?: string;
  inline_context?: string;
  options?: { key?: string; value?: string }[];
};

export type ZernioCreateLeadFormBody = {
  accountId: string;
  name: string;
  questions: ZernioLeadFormQuestionBody[];
  privacyPolicyUrl: string;
  privacyPolicyLinkText?: string;
  followUpActionUrl?: string;
  locale?: string;
  thankYouTitle?: string;
  thankYouBody?: string;
  thankYouButtonText?: string;
  thankYouButtonType?: string;
  thankYouWebsiteUrl?: string;
  isOptimizedForQuality?: boolean;
  // Loose passthrough — only emitted when a duplicate carried one (the createLeadForm
  // schema has no context_card field, so it's never authored from scratch; sending
  // the carried-over value best-effort preserves the intro card on a copy).
  context_card?: unknown;
};

// Map one question to the create body. PREFILLED types must OMIT key/label (Meta
// rejects them); CUSTOM must include key + label, plus inline_context + options
// when present.
export function toCreateQuestionBody(q: LeadFormQuestion): ZernioLeadFormQuestionBody {
  const type = q.type.trim().toUpperCase();
  if (!isCustomQuestionType(type)) {
    return { type };
  }
  const body: ZernioLeadFormQuestionBody = { type };
  if (q.key) body.key = q.key;
  if (q.label) body.label = q.label;
  if (q.inlineContext) body.inline_context = q.inlineContext;
  const opts = (q.options ?? [])
    .map((o) => {
      const out: { key?: string; value?: string } = {};
      if (o.key) out.key = o.key;
      if (o.value) out.value = o.value;
      return out;
    })
    .filter((o) => o.key !== undefined || o.value !== undefined);
  if (opts.length > 0) body.options = opts;
  return body;
}

export function buildCreateLeadFormBody(
  accountId: string,
  input: CreateLeadFormInput,
): ZernioCreateLeadFormBody {
  const body: ZernioCreateLeadFormBody = {
    accountId,
    name: input.name.trim(),
    questions: input.questions.map(toCreateQuestionBody),
    privacyPolicyUrl: input.privacyPolicyUrl.trim(),
  };
  if (input.privacyPolicyLinkText) body.privacyPolicyLinkText = input.privacyPolicyLinkText;
  if (input.followUpActionUrl) body.followUpActionUrl = input.followUpActionUrl;
  if (input.locale) body.locale = input.locale;
  if (input.thankYou) {
    const ty = input.thankYou;
    if (ty.title) body.thankYouTitle = ty.title;
    if (ty.body) body.thankYouBody = ty.body;
    if (ty.buttonText) body.thankYouButtonText = ty.buttonText;
    if (ty.buttonType) body.thankYouButtonType = ty.buttonType;
    if (ty.websiteUrl) body.thankYouWebsiteUrl = ty.websiteUrl;
  }
  if (input.isOptimizedForQuality != null) body.isOptimizedForQuality = input.isOptimizedForQuality;
  // Vendor gap: context_card isn't a create field. We never author one from scratch
  // (input.contextCard is null), but when a duplicate carried one through we pass it
  // back best-effort so the copy keeps its intro card.
  if (input.contextCard != null) body.context_card = input.contextCard;
  return body;
}

// ---------- duplicate prefill: a fetched form → an editable create draft ----------
// Carries the source's questions (incl. CUSTOM), privacy, follow-up, locale and
// thank-you through; suggests a " (copy)" name. status/id are dropped (a new form).
export function leadFormDetailToCreateInput(detail: LeadFormDetail): CreateLeadFormInput {
  return {
    name: detail.name ? `${detail.name} (copy)` : '',
    questions: detail.questions,
    privacyPolicyUrl: detail.privacyPolicyUrl ?? '',
    privacyPolicyLinkText: detail.privacyPolicyLinkText,
    followUpActionUrl: detail.followUpActionUrl,
    locale: detail.locale,
    thankYou: detail.thankYou,
    // Duplicate carries the source's form-type through (Higher-intent stays
    // Higher-intent); a missing value reads as Open.
    isOptimizedForQuality: detail.isOptimizedForQuality ?? null,
    // Carry the intro card through (preserved, not authored) — undefined on a
    // partial/legacy detail reads as none.
    contextCard: detail.contextCard ?? null,
  };
}

// A blank create draft (create-from-scratch start state): a sensible default
// question set (name/email/phone), empty name + privacy URL, Open form type.
export function emptyCreateLeadFormInput(): CreateLeadFormInput {
  return {
    name: '',
    questions: DEFAULT_LEAD_FORM_QUESTION_TYPES.map((type) => ({
      type,
      key: null,
      label: null,
      inlineContext: null,
      options: null,
    })),
    privacyPolicyUrl: '',
    privacyPolicyLinkText: null,
    followUpActionUrl: null,
    locale: null,
    thankYou: null,
    isOptimizedForQuality: null,
    contextCard: null,
  };
}

// ---------- validation (the business-rule guard) ----------
// Meta requires a name, ≥1 question, and an https privacy-policy URL. Returns the
// list of human-readable problems ([] = valid). Used by the service (→ 400) and
// the modal's submit gate.
const HTTPS_URL = /^https:\/\/[^\s.]+\.[^\s]+/i;

export function validateCreateLeadFormInput(input: CreateLeadFormInput): string[] {
  const errors: string[] = [];
  if (input.name.trim() === '') errors.push('A form name is required.');
  if (input.questions.length === 0) errors.push('At least one question is required.');
  const privacy = input.privacyPolicyUrl.trim();
  if (privacy === '') {
    errors.push('A privacy policy URL is required.');
  } else if (!HTTPS_URL.test(privacy)) {
    errors.push('The privacy policy URL must be a full https:// URL.');
  }
  // CUSTOM questions need BOTH a label (lead-facing) and a key (API name) — Meta
  // rejects a CUSTOM question missing either. The key is an API name, so no spaces.
  const customs = input.questions.filter((q) => isCustomQuestionType(q.type));
  if (customs.some((q) => !q.label)) {
    errors.push('Every custom question needs a label.');
  }
  if (customs.some((q) => !q.key)) {
    errors.push('Every custom question needs an API name (field key).');
  }
  if (customs.some((q) => q.key && /\s/.test(q.key))) {
    errors.push('Custom question API names cannot contain spaces.');
  }
  // Multiple-choice options must each carry a displayed value (the lead-facing
  // option text); the stored key is derived/optional.
  if (customs.some((q) => (q.options ?? []).some((o) => !o.value))) {
    errors.push('Every multiple-choice option needs a label.');
  }
  return errors;
}
